import type { SelectValue } from '@shared/ui/select';
import type { OutlookTimeSlotOption } from '@features/outlook-meeting';
import { normalizeDateOnly } from '@shared/libs/date-validation';

export const INVITE_FORM_SCHEMA_VERSION = 3;

export type InviteFormState = {
  invitationExpiresAt: Date | null;
  selectedInvitationChannels: SelectValue[];
  invitationText: string;
  invitationSubject: string;
  inviteAcceptionInfo: string;
  isOutlookMeetingEnabled: boolean;
  outlookMeetingStartTime: OutlookTimeSlotOption | null;
  outlookMeetingEndTime: OutlookTimeSlotOption | null;
  outlookMeetingLocationOrLink: string;
  outlookMeetingComment: string;
  ticketDate: Date | null;
  isSkipTicketSendChecked: boolean;
  isTicketSettingsCollapsed: boolean;
  selectedTicketChannels: SelectValue[];
  ticketText: string;
  ticketSubject: string;
};

export type InviteFormStored = {
  version: typeof INVITE_FORM_SCHEMA_VERSION;
  eventId: number;
  invitationExpiresAt: string | null;
  selectedInvitationChannels: SelectValue[];
  invitationText: string;
  invitationSubject: string;
  inviteAcceptionInfo: string;
  isOutlookMeetingEnabled: boolean;
  outlookMeetingStartTime: OutlookTimeSlotOption | null;
  outlookMeetingEndTime: OutlookTimeSlotOption | null;
  outlookMeetingLocationOrLink: string;
  outlookMeetingComment: string;
  ticketDate: string | null;
  isSkipTicketSendChecked: boolean;
  isTicketSettingsCollapsed: boolean;
  selectedTicketChannels: SelectValue[];
  ticketText: string;
  ticketSubject: string;
};

export const parseStoredDate = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    return new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : normalizeDateOnly(parsed);
};

export const serializeDate = (value: Date | null): string | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizeDateOnly(value);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, '0');
  const day = String(normalized.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const isSelectValue = (value: unknown): value is SelectValue => typeof value === 'string' || typeof value === 'number';

const isSelectValueArray = (value: unknown): value is SelectValue[] =>
  Array.isArray(value) && value.every(isSelectValue);

const isOutlookTimeSlotOption = (value: unknown): value is OutlookTimeSlotOption => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as OutlookTimeSlotOption;
  return typeof candidate.id === 'number' && typeof candidate.label === 'string' && typeof candidate.value === 'string';
};

const isNullableOutlookTimeSlotOption = (value: unknown): value is OutlookTimeSlotOption | null =>
  value === null || isOutlookTimeSlotOption(value);

const MEANINGFUL_FIELD_CHECKS: Array<(state: InviteFormState) => boolean> = [
  (state) => state.invitationExpiresAt !== null,
  (state) => state.ticketDate !== null,
  (state) => state.invitationText.trim().length > 0,
  (state) => state.invitationSubject.trim().length > 0,
  (state) => state.inviteAcceptionInfo.trim().length > 0,
  (state) => state.ticketText.trim().length > 0,
  (state) => state.ticketSubject.trim().length > 0,
  (state) => state.selectedInvitationChannels.length > 0,
  (state) => state.selectedTicketChannels.length > 0,
  (state) => state.isOutlookMeetingEnabled,
];

export const hasMeaningfulInviteFormState = (state: Partial<InviteFormState> | null | undefined): boolean =>
  !!state && MEANINGFUL_FIELD_CHECKS.some((check) => check(state as InviteFormState));

export const serializeInviteFormState = (eventId: number, state: InviteFormState): InviteFormStored => ({
  version: INVITE_FORM_SCHEMA_VERSION,
  eventId,
  invitationExpiresAt: serializeDate(state.invitationExpiresAt),
  selectedInvitationChannels: state.selectedInvitationChannels,
  invitationText: state.invitationText,
  invitationSubject: state.invitationSubject,
  inviteAcceptionInfo: state.inviteAcceptionInfo,
  isOutlookMeetingEnabled: state.isOutlookMeetingEnabled,
  outlookMeetingStartTime: state.outlookMeetingStartTime,
  outlookMeetingEndTime: state.outlookMeetingEndTime,
  outlookMeetingLocationOrLink: state.outlookMeetingLocationOrLink,
  outlookMeetingComment: state.outlookMeetingComment,
  ticketDate: serializeDate(state.ticketDate),
  isSkipTicketSendChecked: state.isSkipTicketSendChecked,
  isTicketSettingsCollapsed: state.isTicketSettingsCollapsed,
  selectedTicketChannels: state.selectedTicketChannels,
  ticketText: state.ticketText,
  ticketSubject: state.ticketSubject,
});

const toRuntimeState = (stored: InviteFormStored): InviteFormState => ({
  invitationExpiresAt: parseStoredDate(stored.invitationExpiresAt),
  selectedInvitationChannels: stored.selectedInvitationChannels,
  invitationText: stored.invitationText,
  invitationSubject: stored.invitationSubject,
  inviteAcceptionInfo: stored.inviteAcceptionInfo,
  isOutlookMeetingEnabled: stored.isOutlookMeetingEnabled,
  outlookMeetingStartTime: stored.outlookMeetingStartTime,
  outlookMeetingEndTime: stored.outlookMeetingEndTime,
  outlookMeetingLocationOrLink: stored.outlookMeetingLocationOrLink,
  outlookMeetingComment: stored.outlookMeetingComment,
  ticketDate: parseStoredDate(stored.ticketDate),
  isSkipTicketSendChecked: stored.isSkipTicketSendChecked,
  isTicketSettingsCollapsed: stored.isTicketSettingsCollapsed,
  selectedTicketChannels: stored.selectedTicketChannels,
  ticketText: stored.ticketText,
  ticketSubject: stored.ticketSubject,
});

type LegacyInviteFormStored = Omit<InviteFormStored, 'version' | 'invitationSubject' | 'ticketSubject'>;

const isValidLegacyStoredPayload = (raw: unknown, eventId: number): raw is LegacyInviteFormStored => {
  if (!raw || typeof raw !== 'object') {
    return false;
  }

  const data = raw as LegacyInviteFormStored;

  if ('version' in data || data.eventId !== eventId) {
    return false;
  }

  return (
    (data.invitationExpiresAt === null || typeof data.invitationExpiresAt === 'string') &&
    isSelectValueArray(data.selectedInvitationChannels) &&
    typeof data.invitationText === 'string' &&
    typeof data.inviteAcceptionInfo === 'string' &&
    typeof data.isOutlookMeetingEnabled === 'boolean' &&
    isNullableOutlookTimeSlotOption(data.outlookMeetingStartTime) &&
    isNullableOutlookTimeSlotOption(data.outlookMeetingEndTime) &&
    typeof data.outlookMeetingLocationOrLink === 'string' &&
    typeof data.outlookMeetingComment === 'string' &&
    (data.ticketDate === null || typeof data.ticketDate === 'string') &&
    typeof data.isSkipTicketSendChecked === 'boolean' &&
    typeof data.isTicketSettingsCollapsed === 'boolean' &&
    isSelectValueArray(data.selectedTicketChannels) &&
    typeof data.ticketText === 'string'
  );
};

const isValidStoredPayload = (raw: unknown, eventId: number): raw is InviteFormStored => {
  if (!raw || typeof raw !== 'object') {
    return false;
  }

  const data = raw as InviteFormStored;

  if (data.version !== INVITE_FORM_SCHEMA_VERSION || data.eventId !== eventId) {
    return false;
  }

  return (
    (data.invitationExpiresAt === null || typeof data.invitationExpiresAt === 'string') &&
    isSelectValueArray(data.selectedInvitationChannels) &&
    typeof data.invitationText === 'string' &&
    typeof data.invitationSubject === 'string' &&
    typeof data.inviteAcceptionInfo === 'string' &&
    typeof data.isOutlookMeetingEnabled === 'boolean' &&
    isNullableOutlookTimeSlotOption(data.outlookMeetingStartTime) &&
    isNullableOutlookTimeSlotOption(data.outlookMeetingEndTime) &&
    typeof data.outlookMeetingLocationOrLink === 'string' &&
    typeof data.outlookMeetingComment === 'string' &&
    (data.ticketDate === null || typeof data.ticketDate === 'string') &&
    typeof data.isSkipTicketSendChecked === 'boolean' &&
    typeof data.isTicketSettingsCollapsed === 'boolean' &&
    isSelectValueArray(data.selectedTicketChannels) &&
    typeof data.ticketText === 'string' &&
    typeof data.ticketSubject === 'string'
  );
};

export const deserializeInviteFormStored = (raw: unknown, eventId: number): InviteFormState | null => {
  if (isValidStoredPayload(raw, eventId)) {
    return toRuntimeState(raw);
  }

  if (isValidLegacyStoredPayload(raw, eventId)) {
    return toRuntimeState({ ...raw, invitationSubject: '', ticketSubject: '', version: INVITE_FORM_SCHEMA_VERSION });
  }

  if (raw !== null && raw !== undefined) {
    console.error('Невалидные данные формы приглашения в sessionStorage');
  }

  return null;
};
